import Content, { ContentNS } from "content/content";
import AutomatedTools from "features/document-page/styles/AutomatedTools.styled";
import useScrollToPageTitle from "features/document-page/hooks/useScrollToPageTitle";
import ToolsButtons from "features/document-page/styles/ToolsButtons.styled";
import PrimaryButton from "features/shared/components/buttons/PrimaryButton.styled";
import SelectFileButton from "features/shared/components/select-file-button/SelectFileButton";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import { Summary } from "features/shared/styles/typography/Summary.styled";
import { FC, ReactNode, useState, type ChangeEvent } from "react";
import { DocProcessingStatus } from "models/doc-processing-status";
import { useAppStore } from "store/store";

import StyledBasicDocumentPage from "./styles/StyledBasicDocumentPage.styled";
import { DocumentRequirement } from "models/checklist/checklist-item/checklist-item";
import { ItemLevelNavigationError } from "models/navigation/item-navigation-error";
import useItemLevelNavigationState, {
  ItemNavigationState,
  ItemTypeNavigationState,
} from "features/shared/hooks/navigation/useItemLevelNavigationState";
import usePaxSession from "features/shared/hooks/usePaxSession";
import { AllowedDocumentsSet } from "models/documents/allowed-document";
import { useSearchParams } from "react-router-dom";
import InfoCard from "../info/InfoCard";
import VisaBanner from "features/shared/components/banners/visa-banner/VisaBanner";
import useVisaBannerReason from "features/shared/components/banners/visa-banner/useVisaBannerReason";
import useUploadSingleSideDocument from "features/shared/hooks/useUploadSingleSideDocument";
import { $SpecialObject } from "i18next/typescript/helpers";
import { TFunctionDetailedResult } from "i18next";
import ChecklistResolutionStatus from "models/checklist/checklist-resolution-status";
import Pax from "models/pax";
import { normalizeName } from "utils";
import { MicroHeader } from "features/shared/styles/typography/MicroHeader.styled";
import SelectPassengerApplyBox from "./styles/SelectPassengerApplyBox.styled";
import SelectPassengerApply from "./styles/SelectPassengerApply.styled";
import Checkbox from "features/shared/components/checkbox/Checkbox";
import Body from "features/shared/styles/typography/Body.styled";
import UploadInstructions, {
  LanguageCode,
} from "assets/images/upload-instructions/upload-instructions";
import UploadInstructionsBlock from "features/document-page/styles/UploadInstructionsBlock.styled";
import ExternalLinkButton from "features/shared/components/link/external-link/ExternalLinkButton";

interface BasicDocumentContentProps {
  itemType: string;
  itemTitle: string;
  shortTitle: string;
  allowedDocsSet: AllowedDocumentsSet;
  infoCard?: ReactNode;
  externalLink?: string | null | undefined;
  item?: DocumentRequirement;
}

interface BasicDocumentByItemProps {
  item: DocumentRequirement;
}

interface BasicDocumentByTypeProps {
  itemType: string;
}

const textDocs = (s: string, args: any = {}) =>
  Content.shared.t(s, { ns: ContentNS.docs, ...args });

const UploadButton: FC<{
  shortTitle: string;
  expectMultiside: boolean;
  onFileSelectMultiSided: (e: ChangeEvent<HTMLInputElement>) => void;
  onFileSelectedSingleSided: (e: ChangeEvent<HTMLInputElement>) => void;
  canScan: boolean;
}> = ({
  shortTitle,
  expectMultiside,
  onFileSelectMultiSided,
  onFileSelectedSingleSided,
  canScan,
}) => {
  const caption = `${textDocs("upload", {
    docTitle: shortTitle,
  })}`;

  if (expectMultiside) {
    return (
      <SelectFileButton
        onChange={onFileSelectMultiSided}
        label={caption}
        primary={!canScan}
      />
    );
  }

  return (
    <SelectFileButton
      onChange={onFileSelectedSingleSided}
      label={caption}
      primary={!canScan}
    />
  );
};

const BasicDocumentContent: FC<BasicDocumentContentProps> = ({
  itemType,
  itemTitle,
  shortTitle,
  allowedDocsSet,
  infoCard,
  externalLink,
  item,
}) => {
  const locale = useAppStore((state) => state.locale);
  const language = locale.split("-")[0] as LanguageCode;

  const paxSession = usePaxSession();
  if (!paxSession) {
    throw new Error("invalid paxSession");
  }

  const visaBannerReason = useVisaBannerReason(item);

  const paxSessions = useAppStore((state) => state.paxSessions);

  useScrollToPageTitle();

  const navigate = useNavigateWithQuery();

  const setProcessingStatus = useAppStore((state) => state.setProcessingStatus);
  const setProcessingMayTakeLong = useAppStore(
    (state) => state.setProcessingMayTakeLong
  );

  const canScan = allowedDocsSet.allowedDocuments.every((doc) => {
    return doc.isScanPossible();
  });

  const canUpload = allowedDocsSet.allowedDocuments.every((doc) => {
    return doc.isUploadPossible();
  });

  const expectMultiside = allowedDocsSet.allowedDocuments.some((doc) => {
    return doc.isMultiSideExpected();
  });

  const isDomTravelerDeclaration = allowedDocsSet.allowedDocuments.some(
    (doc) => {
      return doc.type === "DOM_ELECTRONIC_TRAVELER_DECLARATION";
    }
  );

  const isHndTravelerDeclaration = allowedDocsSet.allowedDocuments.some(
    (doc) => {
      return doc.type === "HND_ELECTRONIC_TRAVELER_DECLARATION";
    }
  );

  const isThaTravelerDeclaration = allowedDocsSet.allowedDocuments.some(
    (doc) => {
      return doc.type === "THA_ELECTRONIC_TRAVELER_DECLARATION";
    }
  );

  const showUploadInstructions =
    isDomTravelerDeclaration ||
    isHndTravelerDeclaration ||
    isThaTravelerDeclaration;

  const eligiblePaxSessionsForMultipaxApply = paxSessions.filter(
    (otherPaxSession) => {
      return (
        paxSession.paxSessionId !== otherPaxSession.paxSessionId &&
        otherPaxSession.checklist.resolutionStatus !==
          ChecklistResolutionStatus.ReadyToFly
      );
    }
  );

  const [additionalPaxSessionIds, setAdditionalPaxSessionIds] = useState<
    string[]
  >([]);

  const multipaxResolutionSupported = allowedDocsSet.allowedDocuments.every(
    (doc) => doc.multipaxResolutionSupported
  );

  const headerText = ():
    | string
    | $SpecialObject
    | TFunctionDetailedResult<string | $SpecialObject> => {
    if (canScan && canUpload) {
      // can both scan and upload
      return textDocs("automated-tools-both", {
        itemTitle: itemTitle,
      });
    }
    if (!canScan && canUpload) {
      // can only upload
      return textDocs("automated-tools-upload", {
        itemTitle: itemTitle,
      });
    }
    // can only scan
    return textDocs("automated-tools-scan", {
      itemTitle: itemTitle,
    });
  };

  const [onFileSelectedSingleSided] = useUploadSingleSideDocument(
    allowedDocsSet.allowedDocuments.some(
      (doc) => doc.expectLongProcessing === true
    ),
    additionalPaxSessionIds,
    allowedDocsSet.allowedDocuments.length === 1
      ? allowedDocsSet.allowedDocuments[0].pageIndexToProcess ?? undefined
      : undefined
  );

  const onFileSelectMultiSided = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!(e.target.files && e.target.files.length > 0)) {
      return;
    }

    navigate("../upload", {
      state: {
        itemType: itemType,
        processingMayTakeLong: allowedDocsSet.allowedDocuments.some(
          (doc) => doc.expectLongProcessing === true
        ),
        inputFile: e.target.files[0],
        additionalPaxSessionIds: additionalPaxSessionIds,
        pageIndexToProcess:
          allowedDocsSet.allowedDocuments.length === 1
            ? allowedDocsSet.allowedDocuments[0].pageIndexToProcess ?? undefined
            : undefined,
      },
    });
  };

  const onScanClick = () => {
    setProcessingStatus(DocProcessingStatus.None, null);
    setProcessingMayTakeLong(null);

    navigate("../capture");
  };

  const getPaxPrintName = (pax: Pax): string => {
    return normalizeName(pax.firstName + " " + pax.surname);
  };

  const handlePaxToggle = (paxSessionId: string) => (value: boolean) => {
    setAdditionalPaxSessionIds((prevState) => {
      if (value) {
        // Add paxSessionId if value is true and it's not already in the array
        return prevState.includes(paxSessionId)
          ? prevState
          : [...prevState, paxSessionId];
      } else {
        // Remove paxSessionId if value is false
        return prevState.filter((id) => id !== paxSessionId);
      }
    });
  };

  return (
    <StyledBasicDocumentPage $hasVisaBanner={visaBannerReason !== null}>
      {item && visaBannerReason !== null ? <VisaBanner item={item} /> : null}
      <AutomatedTools>
        <Summary>{`${headerText()}`}</Summary>

        {showUploadInstructions ? (
          <UploadInstructionsBlock>
            {externalLink ? (
              <ExternalLinkButton href={externalLink}>
                {`${textDocs("upload-instructions.external-link")}`}
              </ExternalLinkButton>
            ) : null}
            {isDomTravelerDeclaration ? (
              <>
                <Body>
                  {`${textDocs(
                    "upload-instructions.dom-traveler-declaration.first"
                  )}`}
                </Body>
                <img
                  src={
                    UploadInstructions.domTravelerDeclaration[language].first
                  }
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    display: "block",
                  }}
                  alt={`${textDocs(
                    "upload-instructions.dom-traveler-declaration.first-alt"
                  )}`}
                />
                <Body>
                  {`${textDocs(
                    "upload-instructions.dom-traveler-declaration.second"
                  )}`}
                </Body>
                <img
                  src={
                    UploadInstructions.domTravelerDeclaration[language].second
                  }
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    display: "block",
                  }}
                  alt={`${textDocs(
                    "upload-instructions.dom-traveler-declaration.second-alt"
                  )}`}
                />
                <Body>
                  {`${textDocs(
                    "upload-instructions.dom-traveler-declaration.third"
                  )}`}
                </Body>
              </>
            ) : null}
            {isHndTravelerDeclaration ? (
              <>
                <Body>
                  {`${textDocs(
                    "upload-instructions.hnd-traveler-declaration.first"
                  )}`}
                </Body>
                <img
                  src={
                    UploadInstructions.hndTravelerDeclaration[language].first
                  }
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    display: "block",
                  }}
                  alt={`${textDocs(
                    "upload-instructions.hnd-traveler-declaration.first-alt"
                  )}`}
                />
                <Body>
                  {`${textDocs(
                    "upload-instructions.hnd-traveler-declaration.second"
                  )}`}
                </Body>
              </>
            ) : null}
            {isThaTravelerDeclaration ? (
              <>
                <Body>
                  {`${textDocs(
                    "upload-instructions.tha-traveler-declaration.first"
                  )}`}
                </Body>
                <img
                  src={
                    UploadInstructions.thaTravelerDeclaration[language].first
                  }
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    display: "block",
                  }}
                  alt={`${textDocs(
                    "upload-instructions.tha-traveler-declaration.first-alt"
                  )}`}
                />
                <Body>
                  {`${textDocs(
                    "upload-instructions.tha-traveler-declaration.second"
                  )}`}
                </Body>
              </>
            ) : null}
          </UploadInstructionsBlock>
        ) : null}

        {multipaxResolutionSupported &&
        eligiblePaxSessionsForMultipaxApply.length > 0 ? (
          <div>
            <MicroHeader>{`${textDocs(
              "multipax-apply-suggestion"
            )}`}</MicroHeader>
            <SelectPassengerApplyBox>
              {eligiblePaxSessionsForMultipaxApply.map(
                (otherPaxSession, i) => {
                  return (
                    <SelectPassengerApply key={`passenger-${i}`}>
                      <Checkbox
                        label={
                          <Body>{getPaxPrintName(otherPaxSession.pax)}</Body>
                        }
                        checked={additionalPaxSessionIds.includes(
                          otherPaxSession.paxSessionId
                        )}
                        onChange={handlePaxToggle(
                          otherPaxSession.paxSessionId
                        )}
                        error={null}
                      />
                    </SelectPassengerApply>
                  );
                }
              )}
            </SelectPassengerApplyBox>
          </div>
        ) : null}
        <ToolsButtons>
          {canScan ? (
            <PrimaryButton onClick={onScanClick}>
              {`${textDocs("scan", {
                docTitle: shortTitle,
              })}`}
            </PrimaryButton>
          ) : null}
          {canUpload ? (
            <UploadButton
              shortTitle={shortTitle}
              expectMultiside={expectMultiside}
              onFileSelectMultiSided={onFileSelectMultiSided}
              onFileSelectedSingleSided={onFileSelectedSingleSided}
              canScan={canScan}
            />
          ) : null}
        </ToolsButtons>
      </AutomatedTools>
      {infoCard ? infoCard : null}
    </StyledBasicDocumentPage>
  );
};

const BasicDocumentByItem: FC<BasicDocumentByItemProps> = ({ item }) => {
  const itemTitle = item.title;

  const shortTitle = item.shortTitle ? item.shortTitle : itemTitle;

  const infoCard = item.externalLink ? <InfoCard item={item} /> : undefined;

  return (
    <BasicDocumentContent
      itemType={item.itemType}
      itemTitle={itemTitle}
      shortTitle={shortTitle}
      allowedDocsSet={item.allowedDocumentsSet}
      infoCard={infoCard}
      externalLink={item.externalLink}
      item={item}
    />
  );
};

const BasicDocumentByType: FC<BasicDocumentByTypeProps> = ({ itemType }) => {
  const textDocs = (s: string) => Content.shared.t(s, { ns: ContentNS.docs });

  const sessionId = useAppStore((state) => state.sessionId);
  const paxSession = usePaxSession();

  const itemTitle = textDocs("itemTitle.TRAVEL_DOC");

  // pick what docs are allowed as travel docs from pax session,
  // as resolution by type is currently only available for TRAVEL_DOC item types
  const allowedDocsSet = paxSession?.checklist.allowedTravelDocumentsSet;

  if (!allowedDocsSet) {
    throw new ItemLevelNavigationError(
      sessionId,
      paxSession?.paxSessionId,
      `allowed travel documents set is not present in pax session`
    );
  }

  return (
    <BasicDocumentContent
      itemType={itemType}
      itemTitle={itemTitle}
      shortTitle={itemTitle}
      allowedDocsSet={allowedDocsSet}
    />
  );
};

const BasicDocumentPage: FC = () => {
  useAppStore((state) => state.locale);

  const sessionId = useAppStore((state) => state.sessionId);
  const paxSession = usePaxSession();

  const [searchParams] = useSearchParams();
  const itemLevelNavigationState = useItemLevelNavigationState(searchParams);

  if (itemLevelNavigationState instanceof ItemNavigationState) {
    // itemId
    const item = itemLevelNavigationState.item;
    if (item instanceof DocumentRequirement === false) {
      throw new ItemLevelNavigationError(
        sessionId,
        paxSession?.paxSessionId,
        `Item is not DocumentRequirement: ${item.id}`
      );
    }

    return <BasicDocumentByItem item={item as DocumentRequirement} />;
  }

  if (itemLevelNavigationState instanceof ItemTypeNavigationState) {
    // itemType
    return <BasicDocumentByType itemType={itemLevelNavigationState.itemType} />;
  }

  // neither itemId or itemType - should not be the case
  throw new ItemLevelNavigationError(
    sessionId,
    paxSession?.paxSessionId,
    `neither itemId or itemType navigation state, which is odd`
  );
};

export default BasicDocumentPage;
